import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import callable from '../../utils/callable'
import useSpinner from '../../context/hooks/useSpinner'
import { APPLICATION_STRINGS } from '../../strings'
import { cardReaderApproved, getCardDataMessage } from '../../utils/orderUtils'
import { hasCardPresentPayment } from '../../utils/paymentTypes'
import { sendExecuteRefundRequest, sendPassthroughRefundRequest } from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { getDeviceSerial } from '../../utils/deviceData'
import PeripheralBridge from '../../utils/peripheralBridge'
import { getSelectedOrder } from '../../selectors/order'
import { getAndroidSDKVersion } from '../../VNAndroidSDK/Selectors'
import { isSDKCompatible } from '../../VNAndroidSDK/Utils'
import { getDeviceMode } from '../../VNMode/Selectors'
import { MODES } from '../../VNMode/Reducer'
import { setCFDBlockingDialog, setConfirmationDialog } from '../../VNDialogs/ActionCreators'
import createDataDogLog from '../../utils/datadog'
import { parseStringToObject } from '../../VNUtil/Strings'
import { incrementInvoiceNumber, invoiceNumber } from '../../utils/orderNumbers'
import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'

const MIN_SUPPORTED_APK_VERSION = '2.20.0'

/**
 * Handles support for card present refund
 *
 * @link https://shift4.atlassian.net/wiki/spaces/venuenext/pages/1863221751
 */
export const useRefundHandler = () => {
  const dispatch = useDispatch()
  const order = useSelector(getSelectedOrder)
  const androidSDKVersion = useSelector(getAndroidSDKVersion)
  const cfdMode = useSelector(getDeviceMode)
  const { startSpinner, stopSpinner } = useSpinner()
  const isMobileViewPort = useGetIsMobileScreen()

  /**
   * Card Present Refund are dependent on Interac (debit) transaction and APK 2.20+
   */
  const hasCardPresentRefund = useCallback(() => {
    const orderPayments = order?.payments ?? []
    if (!orderPayments?.length) return false

    if (!isSDKCompatible(androidSDKVersion, MIN_SUPPORTED_APK_VERSION)) {
      return false
    }

    return hasCardPresentPayment(order)
  }, [androidSDKVersion, order])

  const refundHandler = useCallback(
    (rawData, callback) => {
      const data = typeof rawData === 'string' ? parseStringToObject(rawData) : rawData
      const isApproved = cardReaderApproved(data)
      stopSpinner()

      if (!isMobileViewPort) {
        dispatch(setCFDBlockingDialog(null))
      }

      if (!isApproved) {
        const message = getCardDataMessage(data) ?? 'Card Reader Error'

        dispatch(setConfirmationDialog(APPLICATION_STRINGS.CARD_READER_STATUS, message))
        createDataDogLog('error', '[Refund Error] - Not Approved', { data, order })

        callable(callback, false)
        return
      }

      callable(callback, true, data)
    },
    [stopSpinner, isMobileViewPort, dispatch, order],
  )

  /**
   * After successful manager approval, this function will be called
   */
  const managerSuccessCallback = useCallback(
    (dollarAmount, refundCallback, isActiveRef) => {
      if (!isActiveRef?.current) return

      // non-card present payments trigger handle complete directly
      if (!hasCardPresentPayment(order)) {
        callable(refundCallback)
        return
      }

      const bridge = PeripheralBridge.getBridge()
      bridge?.registerHandler?.('handleResult', (data) => refundHandler(data, refundCallback))
      const paddedInvoiceNumber = invoiceNumber(order.orderNumber)
      const refundInvoiceNumber = incrementInvoiceNumber(paddedInvoiceNumber)
      const payload = {
        amount: dollarAmount,
        hRef: refundInvoiceNumber,
        orderNumber: refundInvoiceNumber,
        uuid: order.uuid,
        deviceSerial: getDeviceSerial(),
      }

      startSpinner()
      if (!isMobileViewPort) {
        dispatch(setCFDBlockingDialog(APPLICATION_STRINGS.CARD_PRESENT_REFUND_DIALOG))
      }

      if (cfdMode === MODES.POS) {
        sendPassthroughRefundRequest(payload)
        return
      }

      sendExecuteRefundRequest(payload)
    },
    [order, startSpinner, isMobileViewPort, cfdMode, refundHandler, dispatch],
  )

  return {
    hasCardPresentRefund,
    refundHandler,
    managerSuccessCallback,
  }
}
